import { EventEmitter, h, Component, Prop, Element, Host, Listen, Event, Watch, State } from '@stencil/core';

import { KEYBOARD } from '../../../config/constants';
import { blockScroll, hasSlotContent, isMobile } from '../../../services/utils';

/**
 * @slot header - Header slot
 * @slot footer - Footer slot
 * @slot - Default slot
 * @part base - The overlay base wrapper
 * @part content - The overlay content wrapper
 * @part footer - The overlay footer
 **/
@Component({
  tag: 'pk-overlay',
  styleUrl: './scss/pk-overlay.scss',
  shadow: true,
})
export class PkOverlay {
  // Own Properties
  // ====================

  private footerRef: HTMLSpanElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkOverlayElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() footerSlotEmpty: boolean;

  // Public Property API
  // ========================

  /** If true, the page behind the modal will be scrollable */
  @Prop({ reflect: true }) allowWindowScroll: boolean = false;

  /** If true, the overlay will not be animated */
  @Prop({ reflect: true }) disableAnimations: boolean = false;

  /** If true, the overlay will not animate from the bottom (or from the top) and only the opacity */
  @Prop({ reflect: true }) disableTransform: boolean = false;

  /** Overlay height */
  @Prop({ reflect: true }) height: string;

  /** Overlay open/close */
  @Prop({ reflect: true, mutable: true }) isOpen: boolean = false;

  /** If true, the overlay will open from the top instead of from the bottom (default) */
  @Prop({ reflect: true }) openFromTop: boolean = false;

  /** Overlay content bottom padding */
  @Prop({ reflect: true }) paddingBottom: string;

  /** Overlay content left padding */
  @Prop({ reflect: true }) paddingLeft: string;

  /** Overlay content right padding */
  @Prop({ reflect: true }) paddingRight: string;

  /** Overlay content top padding */
  @Prop({ reflect: true }) paddingTop: string;

  /** Overlay width */
  @Prop({ reflect: true }) width: string;

  // Prop lifecycle events
  // =======================

  @Watch('isOpen')
  handleWindowScroll() {
    if (this.allowWindowScroll) return;

    blockScroll(this.isOpen, 'overlay');
  }

  // Events section
  // ==============================================

  /** Event emitted on close */
  @Event() pkOverlayClose: EventEmitter<UIEvent>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.handleFooterSlotChange();
    this.handleWindowScroll();
  }

  // Listeners
  // ==============

  @Listen('pkClose')
  closeHandler(event?: UIEvent) {
    this.isOpen = false;
    this.pkOverlayClose.emit(event);
  }

  @Listen('keyup', { target: 'document' })
  handleDocumentKeyDown(event: KeyboardEvent) {
    if (event.key !== KEYBOARD.Escape || !this.isOpen) return;

    this.closeHandler(event);
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleFooterSlotChange = () => {
    if (!this.footerRef) return;

    this.footerSlotEmpty = !hasSlotContent(this.footerRef, 'footer');
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.width && !isMobile() && { '--overlay--width': this.width }),
      ...(this.height && !isMobile() && { '--overlay--height': this.height }),
      ...(this.paddingBottom && { '--overlay--padding-bottom': this.paddingBottom }),
      ...(this.paddingLeft && { '--overlay--padding-left': this.paddingLeft }),
      ...(this.paddingRight && { '--overlay--padding-right': this.paddingRight }),
      ...(this.paddingTop && { '--overlay--padding-top': this.paddingTop }),
    };

    return (
      <Host style={{ ...style }}>
        <div
          class={{
            overlay: true,
            'is-open': this.isOpen,
            'from-top': this.openFromTop,
            'no-transform': this.disableTransform,
            'no-animations': this.disableAnimations,
          }}
          part="base"
        >
          <slot name="header"></slot>
          <div class="content" part="content">
            <slot />
          </div>
          <div
            class={{
              footer: true,
              hidden: this.footerSlotEmpty,
            }}
            ref={(el) => (this.footerRef = el)}
            part="footer"
          >
            <slot name="footer" onSlotchange={this.handleFooterSlotChange}></slot>
          </div>
        </div>
      </Host>
    );
  }
}
